/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable consistent-return */
/* eslint-disable no-undef */
import React, { ReactNode, useEffect, useRef } from 'react';
import {
  TariffExclusionsTable,
  TariffResultsTable,
  TariffFtaTable,
} from './TarrifTypes';
import { effectiveBaseDutyRate, engineFigures } from 'utils/tariffFigures';
import ReactMarkdown from 'react-markdown';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ModuleContentTooltip } from 'components/Common/MUIStyledComponents';
import Switch from '@mui/material/Switch'; // Added import for the switch
import { Tooltip } from '@mui/material';

interface ITariffProps {
  tariffResults: any;
  productValue: number;
  materialPct?: number;
  dutyCost?: number;
  totalCost?: number;
  baseCost?: number;
  displayAdditionalRate?: string;
  tariffCandidates?: any[];
  enabledExclusions?: Record<string, boolean>;
  toggleExclusion?: any;
  ftaOptions?: FtaOption[];
  enabledFtas?: Record<string, boolean>;
  toggleFta?: any;
  explanationText: string;
  aiLoading: boolean;
  adcvdResults?: string;
  aiExplain?: any;
  mpf?: number;
  hmf?: number;
  // NEW IEEPA PROPS
  hasIeepaTariffs?: boolean;
  hideIeepa?: boolean;
  toggleIeepa?: (val: boolean) => void;
}

type FtaOption = { code: string; name: string };

const usd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    Number(n || 0),
  );

const CostSummaryComp: React.FC<ITariffProps> = ({
  tariffResults,
  productValue,
  dutyCost,
  totalCost,
  baseCost,
  displayAdditionalRate,
  tariffCandidates = [],
  enabledExclusions = {},
  toggleExclusion,
  ftaOptions: ftaOptionsProp,
  enabledFtas,
  toggleFta,
  explanationText,
  aiLoading,
  adcvdResults,
  aiExplain,
  mpf,
  hmf,
  hasIeepaTariffs, // Destructure new props
  hideIeepa,
  toggleIeepa,
}) => {
  const d10 = (s: string) => String(s || '').replace(/\D/g, '');
  const safeTariffResults =
    tariffResults && typeof tariffResults === 'object' ? tariffResults : {};

  if ((window as any).DEBUG_CODE) {
    const WANT = d10(String((window as any).DEBUG_CODE));
    const debugLines = Array.isArray(safeTariffResults.results?.lines)
      ? safeTariffResults.results.lines
      : Array.isArray(safeTariffResults.lines)
      ? safeTariffResults.lines
      : [];
    const debugResults = Array.isArray(safeTariffResults.results?.summaryRows)
      ? safeTariffResults.results.summaryRows
      : Array.isArray(safeTariffResults.results)
      ? safeTariffResults.results
      : [];
    // eslint-disable-next-line
    console.log('[TarifFResultsComp DEBUG]', WANT, {
      lines: debugLines.map((ln: any) => ({
        id: ln.id,
        label: ln.label,
        value: ln.value,
        codes: (ln.rows || [])
          .map((r: any) => d10(r._codeDigits || r.code || r.type))
          .filter(Boolean),
      })),
      results: debugResults.map((r: any) => ({
        type: r.type,
        code: r.code,
        digits: d10(r._codeDigits || r.code || r.type),
      })),
    });
  }

  // --- Normalize "live" vs "snapshot" shapes --------------------------------
  const isSnapshot = Boolean(
    safeTariffResults.inputs &&
      safeTariffResults.base &&
      safeTariffResults.results,
  );

  const savedExplanation: string = isSnapshot
    ? String(safeTariffResults.explanation?.text || '')
    : '';

  // per-line data
  const linesRaw: any[] = isSnapshot
    ? Array.isArray(safeTariffResults.results?.lines)
      ? safeTariffResults.results.lines
      : []
    : Array.isArray(safeTariffResults.lines)
    ? safeTariffResults.lines
    : [];

  const explanationToShow = isSnapshot
    ? savedExplanation
    : explanationText || '';
  const aiBusy = isSnapshot ? false : aiLoading;

  const lines = Array.isArray(linesRaw) ? linesRaw : [];
  const hasSplit = lines.length > 0;

  // rows shown in "Duty Summary"
  const summaryRows: any[] = isSnapshot
    ? Array.isArray(safeTariffResults.results?.summaryRows)
      ? safeTariffResults.results.summaryRows
      : []
    : Array.isArray(safeTariffResults.results)
    ? safeTariffResults.results
    : [];

  // base duty rate and description
  const baseDutyRate: string =
    (isSnapshot
      ? safeTariffResults.base?.baseDutyRate
      : safeTariffResults.baseDutyRate) ??
    summaryRows.find(
      (r: any) => r?.type === 'Base Duty' || r?.type === 'Base Duty (Column 2)',
    )?.rate ??
    '—';

  // The engine's figures: live results carry them as engineMath, a saved
  // snapshot as math.
  const engineMath = isSnapshot
    ? safeTariffResults.math
    : safeTariffResults.engineMath;
  const baseDutyRateDisplay = effectiveBaseDutyRate(
    baseDutyRate,
    lines,
    engineMath,
    isSnapshot
      ? Number(safeTariffResults.inputs?.productValue || 0)
      : productValue,
  );

  const productDesc: string =
    (isSnapshot
      ? safeTariffResults.base?.productDesc
      : safeTariffResults.productDesc) ?? '';

  const additionalRateDisplay: string =
    displayAdditionalRate ??
    (isSnapshot
      ? safeTariffResults.math?.additionalDutyRateDisplay
      : safeTariffResults.additionalDutyRate) ??
    '—';

  const mpfToShow: number | undefined =
    typeof mpf === 'number'
      ? mpf
      : isSnapshot
      ? Number(safeTariffResults.math?.mpf ?? NaN)
      : undefined;

  const hmfToShow: number | undefined =
    typeof hmf === 'number'
      ? hmf
      : isSnapshot
      ? Number(safeTariffResults.math?.hmf ?? NaN)
      : undefined;

  const ftaOptions: FtaOption[] =
    ftaOptionsProp ??
    (isSnapshot
      ? (safeTariffResults.results?.ftaOptions as FtaOption[]) ?? []
      : []);

  const candidatesForExclusions: any[] = tariffCandidates.length
    ? tariffCandidates
    : isSnapshot
    ? safeTariffResults.results?.optionalExclusions ?? []
    : safeTariffResults.optionalExclusions ?? [];

  const dutyCostDisplay =
    dutyCost ??
    (isSnapshot ? Number(safeTariffResults.math?.dutyCost || 0) : 0);
  const totalCostDisplay =
    totalCost ??
    (isSnapshot ? Number(safeTariffResults.math?.totalCost || 0) : 0);

  const [dots, setDots] = React.useState('');

  useEffect(() => {
    if (!aiLoading) {
      setDots('');
      return;
    }
    const id = setInterval(() => {
      setDots((d) => (d.length >= 3 ? '' : `${d}.`));
    }, 350);
    return () => clearInterval(id);
  }, [aiLoading]);

  const shouldShowAiSection =
    explanationToShow.length > 0 ||
    (!isSnapshot && (Boolean(aiExplain) || aiBusy));

  const baseCostDisplay =
    typeof baseCost === 'number'
      ? baseCost
      : isSnapshot
      ? engineFigures(engineMath).baseDuty
      : 0;

  return (
    <>
      <div
        style={{
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {/* RIGHT */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className='tariff-container-right '>
            <div className='screening-ai-label-text'>Cost Summary</div>

            <div className='screening-ai-row'>
              <div className='tariff-desc-text'>Base Duty Rate:</div>
              <div className='tariff-desc-text'>{baseDutyRateDisplay}</div>
            </div>

            <div className='screening-ai-row'>
              <div className='tariff-desc-text'>Base Duty Cost:</div>

              <div className='tariff-desc-text'>{usd(baseCostDisplay)}</div>
            </div>

            <div className='screening-ai-row'>
              <div className='tariff-desc-text'>Additional Duty Rate:</div>
              <div className='tariff-desc-text'>{additionalRateDisplay}</div>
            </div>

            <div className='screening-ai-row'>
              <div className='tariff-desc-text'>Additional Duty Cost:</div>
              <div className='tariff-desc-text'>
                {usd(dutyCostDisplay - baseCostDisplay)}
              </div>
            </div>

            <div className='screening-ai-row'>
              <div className='screening-ai-label-text'>Total Duty Cost:</div>
              <div className='screening-ai-label-text'>
                {usd(dutyCostDisplay)}
              </div>
            </div>

            {typeof mpfToShow === 'number' && mpfToShow > 0 && (
              <div className='screening-ai-row'>
                <div className='tariff-desc-text'>
                  <ModuleContentTooltip
                    title={
                      <p className='module-header-tooltip-text'>
                        Merchandise Processing Fee
                      </p>
                    }
                    placement='top'
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          display: 'flex',
                          padding:
                            'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          alignSelf: 'stretch',
                          borderRadius: 'var(--radius-md, 8px)',
                          background: 'var(--Background-200, var(--bg-colors))',
                          color: 'var(--text-prim25)',
                          maxWidth: '400px',
                          '& .MuiTooltip-arrow': {
                            color: 'var(--Background-200, var(--bg-colors))',
                          },
                        },
                      },
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                    >
                      <path
                        d='M9 8.25C8.80109 8.25 8.61033 8.32902 8.46967 8.46967C8.32902 8.61032 8.25 8.80109 8.25 9V12C8.25 12.1989 8.32902 12.3897 8.46967 12.5303C8.61033 12.671 8.80109 12.75 9 12.75C9.19892 12.75 9.38968 12.671 9.53033 12.5303C9.67099 12.3897 9.75 12.1989 9.75 12V9C9.75 8.80109 9.67099 8.61032 9.53033 8.46967C9.38968 8.32902 9.19892 8.25 9 8.25ZM9.285 5.31C9.10241 5.23499 8.8976 5.23499 8.715 5.31C8.62294 5.3457 8.53883 5.39922 8.4675 5.4675C8.40126 5.5404 8.34799 5.62411 8.31 5.715C8.26802 5.80401 8.24747 5.90162 8.25 6C8.24943 6.0987 8.26835 6.19655 8.30568 6.28793C8.343 6.37931 8.39799 6.46242 8.4675 6.5325C8.5404 6.59875 8.62412 6.65202 8.715 6.69C8.82863 6.73668 8.95198 6.75474 9.07421 6.74258C9.19645 6.73043 9.31383 6.68844 9.41604 6.6203C9.51825 6.55216 9.60215 6.45996 9.66039 6.3518C9.71862 6.24364 9.74939 6.12284 9.75 6C9.74724 5.80142 9.66955 5.61123 9.5325 5.4675C9.46118 5.39922 9.37707 5.3457 9.285 5.31ZM9 1.5C7.51664 1.5 6.0666 1.93987 4.83323 2.76398C3.59986 3.58809 2.63856 4.75943 2.07091 6.12987C1.50325 7.50032 1.35472 9.00832 1.64411 10.4632C1.9335 11.918 2.64781 13.2544 3.6967 14.3033C4.7456 15.3522 6.08197 16.0665 7.53683 16.3559C8.99168 16.6453 10.4997 16.4968 11.8701 15.9291C13.2406 15.3614 14.4119 14.4001 15.236 13.1668C16.0601 11.9334 16.5 10.4834 16.5 9C16.5 8.01509 16.306 7.03982 15.9291 6.12987C15.5522 5.21993 14.9997 4.39314 14.3033 3.6967C13.6069 3.00026 12.7801 2.44781 11.8701 2.0709C10.9602 1.69399 9.98492 1.5 9 1.5ZM9 15C7.81332 15 6.65328 14.6481 5.66658 13.9888C4.67989 13.3295 3.91085 12.3925 3.45673 11.2961C3.0026 10.1997 2.88378 8.99334 3.11529 7.82946C3.3468 6.66557 3.91825 5.59647 4.75736 4.75736C5.59648 3.91824 6.66558 3.3468 7.82946 3.11529C8.99335 2.88378 10.1997 3.0026 11.2961 3.45672C12.3925 3.91085 13.3295 4.67988 13.9888 5.66658C14.6481 6.65327 15 7.81331 15 9C15 10.5913 14.3679 12.1174 13.2426 13.2426C12.1174 14.3679 10.5913 15 9 15Z'
                        fill='var(--text-secondary)'
                      />
                    </svg>
                  </ModuleContentTooltip>{' '}
                  MPF:
                </div>
                <div className='tariff-desc-text'>{usd(mpfToShow)}</div>
              </div>
            )}

            {typeof hmfToShow === 'number' && hmfToShow > 0 && (
              <div className='screening-ai-row'>
                <div className='tariff-desc-text'>
                  {' '}
                  <ModuleContentTooltip
                    title={
                      <p className='module-header-tooltip-text'>
                        Harbor Maintenance Fee
                      </p>
                    }
                    placement='top'
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          display: 'flex',
                          padding:
                            'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          alignSelf: 'stretch',
                          borderRadius: 'var(--radius-md, 8px)',
                          background: 'var(--Background-200, var(--bg-colors))',
                          color: 'var(--text-prim25)',
                          maxWidth: '400px',
                          '& .MuiTooltip-arrow': {
                            color: 'var(--Background-200, var(--bg-colors))',
                          },
                        },
                      },
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                    >
                      <path
                        d='M9 8.25C8.80109 8.25 8.61033 8.32902 8.46967 8.46967C8.32902 8.61032 8.25 8.80109 8.25 9V12C8.25 12.1989 8.32902 12.3897 8.46967 12.5303C8.61033 12.671 8.80109 12.75 9 12.75C9.19892 12.75 9.38968 12.671 9.53033 12.5303C9.67099 12.3897 9.75 12.1989 9.75 12V9C9.75 8.80109 9.67099 8.61032 9.53033 8.46967C9.38968 8.32902 9.19892 8.25 9 8.25ZM9.285 5.31C9.10241 5.23499 8.8976 5.23499 8.715 5.31C8.62294 5.3457 8.53883 5.39922 8.4675 5.4675C8.40126 5.5404 8.34799 5.62411 8.31 5.715C8.26802 5.80401 8.24747 5.90162 8.25 6C8.24943 6.0987 8.26835 6.19655 8.30568 6.28793C8.343 6.37931 8.39799 6.46242 8.4675 6.5325C8.5404 6.59875 8.62412 6.65202 8.715 6.69C8.82863 6.73668 8.95198 6.75474 9.07421 6.74258C9.19645 6.73043 9.31383 6.68844 9.41604 6.6203C9.51825 6.55216 9.60215 6.45996 9.66039 6.3518C9.71862 6.24364 9.74939 6.12284 9.75 6C9.74724 5.80142 9.66955 5.61123 9.5325 5.4675C9.46118 5.39922 9.37707 5.3457 9.285 5.31ZM9 1.5C7.51664 1.5 6.0666 1.93987 4.83323 2.76398C3.59986 3.58809 2.63856 4.75943 2.07091 6.12987C1.50325 7.50032 1.35472 9.00832 1.64411 10.4632C1.9335 11.918 2.64781 13.2544 3.6967 14.3033C4.7456 15.3522 6.08197 16.0665 7.53683 16.3559C8.99168 16.6453 10.4997 16.4968 11.8701 15.9291C13.2406 15.3614 14.4119 14.4001 15.236 13.1668C16.0601 11.9334 16.5 10.4834 16.5 9C16.5 8.01509 16.306 7.03982 15.9291 6.12987C15.5522 5.21993 14.9997 4.39314 14.3033 3.6967C13.6069 3.00026 12.7801 2.44781 11.8701 2.0709C10.9602 1.69399 9.98492 1.5 9 1.5ZM9 15C7.81332 15 6.65328 14.6481 5.66658 13.9888C4.67989 13.3295 3.91085 12.3925 3.45673 11.2961C3.0026 10.1997 2.88378 8.99334 3.11529 7.82946C3.3468 6.66557 3.91825 5.59647 4.75736 4.75736C5.59648 3.91824 6.66558 3.3468 7.82946 3.11529C8.99335 2.88378 10.1997 3.0026 11.2961 3.45672C12.3925 3.91085 13.3295 4.67988 13.9888 5.66658C14.6481 6.65327 15 7.81331 15 9C15 10.5913 14.3679 12.1174 13.2426 13.2426C12.1174 14.3679 10.5913 15 9 15Z'
                        fill='var(--text-secondary)'
                      />
                    </svg>
                  </ModuleContentTooltip>{' '}
                  HMF:
                </div>
                <div className='tariff-desc-text'>{usd(hmfToShow)}</div>
              </div>
            )}

            <div className='screening-ai-row'>
              <div className='screening-ai-label-text'>Duties and Fees:</div>
              <div className='screening-ai-label-text'>
                {usd(
                  (Number(dutyCostDisplay) || 0) +
                    (Number(hmfToShow) || 0) +
                    (Number(mpfToShow) || 0),
                )}
              </div>
            </div>

            <div className='tariff-results-line-div'></div>

            <div className='screening-ai-row' style={{ marginTop: 'auto' }}>
              <div
                className='screening-ai-label-text'
                style={{ marginRight: '10px' }}
              >
                Total Cost:
              </div>
              <div className='tariff-total-cost'>{usd(totalCostDisplay)}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CostSummaryComp;
